'use client';
import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { getPayAppUrl } from '@financial/app-js-interfaces';
import { TopLayerWrapper } from '@financial/common-styled-elements';
import { getShortenUrl } from '@financial/common-utils';
import classNames from '../node_modules/.pnpm/classnames@2.5.1/node_modules/classnames/bind.mjs';
import { drawQRinHTMLCanvas } from '../qrcode.mjs';
import { DEFAULT_SERVICE_MENU_OPTION, OS_ENVIRONMENT, APP_OS_ENV, ENVIRONMENT, APP_ENVIRONMENT } from '../share/constants.mjs';
import { getNewPayOrigin, getInitialAppEnv, convertPhase } from '../share/utils.mjs';
import IconRefresh from './components/IconRefresh.mjs';
import ImgQrLoading from './components/ImgQrLoading.mjs';
import { AppSelector, OSSelector } from './components/Selector.mjs';
import useUrl from './hooks/useUrl.mjs';
import style from './QRBridge.module.mjs';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const cx = classNames.bind(style);
const QRBridge = _ref => {
  let {
    landingUrl = 'https://new-m.pay.naver.com/mydata/home',
    title = /*#__PURE__*/jsxs(Fragment, {
      children: ["QR\uCF54\uB4DC\uB97C \uCC0D\uC5B4", /*#__PURE__*/jsx("br", {}), "\uBAA8\uBC14\uC77C\uB85C \uB0B4 \uC790\uC0B0\uC744 \uB9CC\uB098\uBCF4\uC138\uC694"]
    }),
    isShow = false,
    onClose,
    applyShortenUrl: injectedApplyShortenUrl = false,
    phase = 'real',
    supportApps = ['naverapp', 'payapp'],
    inappbrowser = false,
    serviceMenuOption = DEFAULT_SERVICE_MENU_OPTION
  } = _ref;
  const ref = useRef(null);
  const payOrigin = useMemo(() => getNewPayOrigin(phase), [phase]);
  const applyShortenUrl = injectedApplyShortenUrl || encodeURIComponent(landingUrl).length > 100;
  const [appEnv, setAppEnv] = useState(getInitialAppEnv(supportApps));
  const [osEnv, setOsEnv] = useState(OS_ENVIRONMENT.ANDROID);
  const env = useMemo(() => APP_OS_ENV[appEnv + osEnv], [appEnv, osEnv]);
  const {
    targetUrl,
    shortenUrl,
    setShortenUrl,
    resetShortenUrl,
    expireShortenUrl,
    renewShortenUrl,
    showRetry
  } = useUrl({
    phase: convertPhase(phase),
    env,
    shouldInAppBrowserOpen: inappbrowser,
    landingUrl,
    serviceMenuOption
  });
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  const currentShortenUrl = useMemo(() => shortenUrl[env], [shortenUrl, env]);
  const currentTargetUrl = useMemo(() => targetUrl[env], [targetUrl, env]);
  const [waitingForShortening, setWaitingForShortening] = useState(Object.values(ENVIRONMENT).reduce((status, environment) => {
    status[environment] = true;
    return status;
  }, {}));
  const fetchShortenUrl = useCallback(async () => {
    try {
      const isPayAppIOS = env === APP_OS_ENV[APP_ENVIRONMENT.PAY_APP + OS_ENVIRONMENT.IOS];
      /**
       * IOS 페이앱에서 단축 URL을 사용하는 경우 페이앱이 아닌 네이버앱으로 이동하는 이슈가 있어서
       * 단축 URL을 landingUrl에만 적용함
       *
       * URL 길이에 따른 인식 속도는 노운이슈
       * @see https://oss.fin.navercorp.com/financial-member/QA/issues/557#issuecomment-765749
       */
      const shortenedUrl = await getShortenUrl(payOrigin, isPayAppIOS ? landingUrl : currentTargetUrl);
      setShortenUrl(prev => ({
        ...prev,
        [env]: isPayAppIOS ? getPayAppUrl({
          phase: convertPhase(phase),
          deviceType: 'ios',
          ...(inappbrowser ? {
            shouldInAppBrowserOpen: inappbrowser,
            landingUrl: shortenedUrl
          } : {
            shouldInAppBrowserOpen: inappbrowser,
            serviceMenuOption
          })
        }) : shortenedUrl
      }));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('QR URL shortening failed');
    } finally {
      setWaitingForShortening(prev => ({
        ...prev,
        [env]: false
      }));
    }
  }, [currentTargetUrl, env, inappbrowser, landingUrl, payOrigin, phase, serviceMenuOption, setShortenUrl]);
  const handleRetry = useCallback(() => {
    renewShortenUrl();
    fetchShortenUrl();
  }, [renewShortenUrl, fetchShortenUrl]);
  useEffect(() => resetShortenUrl(), [landingUrl, resetShortenUrl]);
  useEffect(function initShortenUrl() {
    if (isShow && applyShortenUrl && !currentShortenUrl) {
      fetchShortenUrl();
      expireShortenUrl();
    }
    if (isShow && !applyShortenUrl) {
      setWaitingForShortening(prev => ({
        ...prev,
        [env]: false
      }));
    }
  }, [isShow, applyShortenUrl, fetchShortenUrl, currentShortenUrl, expireShortenUrl, env]);
  useEffect(function drawQR() {
    if (isShow && ref.current && !waitingForShortening[env]) {
      drawQRinHTMLCanvas(currentShortenUrl || currentTargetUrl, ref.current, {
        width: 120,
        scale: 1,
        margin: 0
      });
    }
  }, [isShow, currentShortenUrl, currentTargetUrl, applyShortenUrl, waitingForShortening, env]);
  return /*#__PURE__*/jsx(TopLayerWrapper, {
    shouldShow: isShow,
    closeFn: handleClose,
    preventBackgroundScroll: true,
    children: /*#__PURE__*/jsxs("div", {
      className: cx('container'),
      children: [/*#__PURE__*/jsx("h3", {
        className: "blind",
        children: "\uB124\uC774\uBC84\uD398\uC774 \uBAA8\uBC14\uC77C QR \uBE0C\uB9BF\uC9C0"
      }), /*#__PURE__*/jsxs("div", {
        className: cx('inner'),
        children: [/*#__PURE__*/jsx("p", {
          className: cx('description'),
          children: title
        }), ['naverapp', 'payapp'].every(_appEnv => supportApps.includes(_appEnv)) && /*#__PURE__*/jsx(AppSelector, {
          appEnv: appEnv,
          setAppEnv: setAppEnv
        }), /*#__PURE__*/jsx(OSSelector, {
          osEnv: osEnv,
          setOsEnv: setOsEnv
        }), /*#__PURE__*/jsxs("div", {
          className: cx('qrcode-area'),
          children: [/*#__PURE__*/jsx("strong", {
            className: "blind",
            children: "QR\uCF54\uB4DC \uD655\uC778\uD558\uAE30"
          }), waitingForShortening[env] ? /*#__PURE__*/jsxs("div", {
            className: cx('loading'),
            children: [/*#__PURE__*/jsx(ImgQrLoading, {
              width: 110,
              height: 110
            }), /*#__PURE__*/jsx("p", {
              className: "blind",
              children: "QR\uCF54\uB4DC\uB97C \uBD88\uB7EC\uC624\uB294 \uC911\uC785\uB2C8\uB2E4."
            })]
          }) : /*#__PURE__*/jsx("canvas", {
            ref: ref
          }), showRetry[env] && /*#__PURE__*/jsxs("div", {
            className: cx('retry'),
            children: [/*#__PURE__*/jsx("p", {
              className: "blind",
              children: "QR\uCF54\uB4DC \uC720\uD6A8\uC2DC\uAC04\uC774 \uC9C0\uB0AC\uC2B5\uB2C8\uB2E4. QR\uCF54\uB4DC\uB97C \uB2E4\uC2DC \uB178\uCD9C\uD558\uB824\uBA74 \uC7AC\uC2DC\uB3C4 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC138\uC694"
            }), /*#__PURE__*/jsxs("button", {
              type: "button",
              onClick: handleRetry,
              className: cx('button-retry'),
              children: [/*#__PURE__*/jsx("span", {
                className: cx('icon'),
                children: /*#__PURE__*/jsx(IconRefresh, {
                  width: 16,
                  height: 17
                })
              }), "\uC7AC\uC2DC\uB3C4"]
            })]
          })]
        }), /*#__PURE__*/jsx("div", {
          className: cx('close-area'),
          children: /*#__PURE__*/jsx("button", {
            type: "button",
            onClick: handleClose,
            className: cx('button-close'),
            children: "\uB2EB\uAE30"
          })
        }), /*#__PURE__*/jsxs("p", {
          className: "blind",
          children: [env, "QR URL:", currentShortenUrl || currentTargetUrl]
        })]
      })]
    })
  });
};

export { QRBridge };
